/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import styles from './styles';
import { Grid, IconButton, TextField, Typography } from '@mui/material';
import Datagrid from '../../Datagrid';
import PropTypes from 'prop-types';
import { AddRounded, CancelPresentationRounded, DeleteRounded, LogoutOutlined, RemoveRounded, SaveRounded } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import useUnsavedChanges from '../../../../hooks/useUnsavedChanges';
import { renderCell } from '../../Datagrid/table_config';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import ModalDialog from '../../ModalDialog';
import { columns, parametriTablecColumns } from './table_config';
import { HYDRAULICS_RUN_DETAILS, MAX_ROW_LIMITS, PARAMETER_ANALYSIS_TYPE, PARAMETER_ANALYSIS_TYPE_VALUE } from '../../../../utils/constants';
import SelectEditValueTypes from '../../SelectEditValueTypes';
import { addNewParameterRow, createHydraulicParameter, deleteHydraulicParameter, listHydraulicsParameter, runParametricAnalysis, updateHydraulicParameter } from '../../../../store/actions/hydraulicsParameterAnalysis';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import { UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { COLORS } from '../../../../shared/config/colors';

function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}

const ParametricAnalysisModal = ({
  onCloseClick,
  selectedJob,
  isOpen,
  rowData
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { unit, convertUnitSystem } = useUnitConversion();
  const hydraulics = useSelector((state) => state.hydraulics);
  const hydraulicsParameter = useSelector((state) => state.hydraulicsParameterAnalysis);
  const depthPoints = hydraulics?.units?.run_response?.depth_points;
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [runDetails, setRundetails] = useState([]);
  const [loader, setLoader] = useState(false);
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);

  useEffect(() => {
    if (rowData?.run_details) {
      const { drillStrings = [], drillFluids = [] } = hydraulics?.drillDetails?.hydraulicsDrillDetails || {};
      const drillString = drillStrings.find(item => item._id === rowData?.run_details.drillStringsId) || drillStrings[0];
      const drillFluid = drillFluids.find(item => item._id === rowData?.run_details.drillFluidsId) || drillFluids[0];
      const runDetails = {
        ...rowData.run_details,
        _id: generateRandom(),
        rpm: rowData?.run_details?.rpm ?? 0.0,
        bit_depth_offset: rowData?.run_details?.bit_depth_offset ?? 0.0,
        drillStringsId: drillString
          ? `${drillString.name.trim()} @ ${drillString.MD.toFixed(hydraulics?.drillDetails?.units?.MD?.decimals)}${depthPoints?.md?.label}`
          : '',
        drillFluidsId: drillFluid
          ? `${drillFluid.name.trim()} @ ${drillFluid.MD.toFixed(hydraulics?.drillDetails?.units?.MD?.decimals)}${depthPoints?.md?.label}`
          : ''
      };
      setRundetails([runDetails]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!hydraulicsParameter.loading) {
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id
      };
      dispatch(listHydraulicsParameter(payload));
    }
  }, []);

  useEffect(() => {
    if (hydraulicsParameter?.inputs) {
      const resultArray = (hydraulicsParameter?.inputs || []).map((item) => {
        const matchedKey = Object.keys(PARAMETER_ANALYSIS_TYPE_VALUE)
          .find(key => PARAMETER_ANALYSIS_TYPE_VALUE[key] === item.parameter) || item.parameter;

        const decimalPlaces = hydraulicsParameter?.units?.inputs?.[matchedKey]?.decimals ?? 2;
        return {
          ...item,
          parameter: PARAMETER_ANALYSIS_TYPE_VALUE[matchedKey] || matchedKey,
          values: item.values?.map(val => {
            if (val === '' || val === null || val === undefined) return val;
            const numericValue = Number(val);
            if (isNaN(numericValue)) return val;

            return parseFloat(numericValue.toFixed(decimalPlaces));
          }) || [],
          _id: item._id,
        };
      });
      setProperties(resultArray);
    }
  }, [hydraulicsParameter]);

  useEffect(() => {
    if (loader) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loader]);

  useEffect(() => {
    if (properties[0]?.type === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'parameter', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'parameter');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
    setProperties([]);
  };

  const handleAddParameter = () => {
    if (properties[0]?.isNewRow) return;
    dispatch(addNewParameterRow());
  };

  const runHydraulicsProxy = (hydraulicsRunID) => {
    const body = {
      query: selectedJob?.fromWells
        ? {
            wellsInfoId: selectedJob?.wellsInfoId
          }
        : {
            jobId: selectedJob?._id
          },
      runId: hydraulicsRunID
    };
    dispatch(runParametricAnalysis(body, () => {
      onCloseClick();
    }, () => {
      setLoader(false);
    }));
  };

  const getModalTitle = () => {
    return (
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            {'Parametric Analysis'}
          </Grid>
          <Grid item>
            <IconButton
            disabled={properties.some(item => item.isDirty === true) || hydraulicsParameter?.inputs[0]?.isNewRow || hydraulicsParameter?.inputs[0]?.isDirty || hydraulicsParameter?.inputs?.length < 1 || hydraulicsParameter.loading}
            onClick={() => {
              runHydraulicsProxy(rowData?._id);
              setLoader(true);
            }}
            >
              <LogoutOutlined fontSize='large' />
            </IconButton>
            <IconButton
              onClick={closeHandler}
            >
              <CancelPresentationRounded fontSize='large' />
            </IconButton>
          </Grid>
        </Grid>
    );
  };

  const updateData = (id, field, value) => {
    setProperties((prevProperties) => {
      const existingParameter = prevProperties.find(
        (item) => item.parameter === value && item._id !== id
      );
      if (field === 'parameter' && existingParameter) {
        dispatch(
          enqueueSnackbar(
            `${existingParameter.parameter} already selected.`,
            'warning',
            new Date().getTime() + Math.random()
          )
        );
        return prevProperties;
      }
      return prevProperties.map((item) => {
        if (item._id === id) {
          return {
            ...item,
            [field]: value,
            isDirty: JSON.stringify(value) !== JSON.stringify(item[field]),
          };
        }
        return item;
      });
    });
  };

  const setIsDirtyForId = (targetId) => {
    setProperties(prevData =>
      prevData.map(item =>
        item._id === targetId ? { ...item, isDirty: true } : item
      )
    );
  };

  const getFilteredParameterTypes = (properties = [], currentValue) => {
    const existingParameters = properties.map(item => item.parameter);
    return PARAMETER_ANALYSIS_TYPE.filter(param => !existingParameters.includes(param) || param === currentValue);
  };

  const renderParameterDropDown = (params, properties = []) => {
    const currentValue = params?.value ?? '';
    const filteredData = getFilteredParameterTypes(properties, currentValue);

    return (
      <SelectEditValueTypes
        {...params}
        enableSort={false}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={filteredData}
        value={currentValue}
      />
    );
  };
  const renderValueCell = ({ row }) => {
    const [values, setValues] = useState(row?.values?.length ? row?.values : ['', '']);
    useEffect(() => {
      setValues(row?.values?.length ? row.values : ['', '']);
    }, [row.values]);

    const handleChange = (index, newValue) => {
      const updatedValues = [...values];
      updatedValues[index] = newValue === '' ? '' : Number(newValue);
      setValues(updatedValues);
      updateData(row._id, 'values', updatedValues);
    };

    const addInputField = () => {
      if (values.length < 4) {
        setValues([...values, '']);
      }
    };

    const removeInputField = () => {
      if (values.length > 2) {
        const updatedValues = values.slice(0, -1);
        setValues(updatedValues);
        updateData(row._id, 'values', updatedValues);
      }
    };

    return (
      <Grid container spacing={1} alignItems="center" justifyContent="center">
        {values.map((val, index) => (
          <Grid item key={index}>
            <TextField
              type="number"
              value={val}
              onChange={(e) => handleChange(index, e.target.value)}
              size="small"
              inputProps={{
                inputMode: 'decimal',
                pattern: '[0-9]*[.,]?[0-9]*',
                style: {
                  appearance: 'textfield'
                }
              }}
              sx={{
                width: '100px',
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                }
              }}
            />

          </Grid>
        ))}
        <Grid item>
          <IconButton onClick={() => {
            setIsDirtyForId(row?._id);
            addInputField();
          }} disabled={values.length >= 4}>
            <AddRounded />
          </IconButton>
          <IconButton onClick={() => {
            setIsDirtyForId(row?._id);
            removeInputField();
          }} disabled={values.length <= 2}>
            <RemoveRounded />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const validateParameterDetails = (details) => {
    const { _id, values, parameter } = details;
    const min_rop = hydraulicsParameter.units.inputs.rop.min;
    const max_rop = hydraulicsParameter.units.inputs.rop.max;
    const min_sbp = hydraulicsParameter.units.inputs.sbp.min;
    const min_flow_in = hydraulicsParameter.units.inputs.flow_in.min;
    const min_rpm = hydraulicsParameter.units.inputs.rpm.min;
    const max_rpm = hydraulicsParameter.units.inputs.rpm.max;
    const min_mw = hydraulicsParameter.units.inputs.mw.min;
    const max_mw = hydraulicsParameter.units.inputs.mw.max;

    if (new Set(values).size !== values.length) {
      return showError('Please check Value. Should not have duplicate values', _id);
    }

    if (parameter === 'SBP' && values.some(value => value < min_sbp)) {
      return showError(HYDRAULICS_RUN_DETAILS.SBP, _id);
    }
    if (parameter === 'Flow Rate' && values.some(value => value < min_flow_in)) {
      return showError(HYDRAULICS_RUN_DETAILS.FLOW_IN, _id);
    }
    if (parameter === 'RPM' && values.some(value => value < min_rpm || value > max_rpm)) {
      return showError(`RPM should be between ${min_rpm} and ${max_rpm}`, _id);
    }
    if (parameter === 'ROP' && values.some(value => value < min_rop || value > max_rop)) {
      return showError(`ROP should be between ${min_rop} and ${max_rop}`, _id);
    }
    if (parameter === 'MW' && values.some(value => value < min_mw || value > max_mw)) {
      return showError(`Mud Weight should be between ${min_mw} and ${max_mw}`, _id);
    }
    return true;
  };

  const checkHydraulicsParameterValues = (value) => {
    const row = value;
    const invalidFields = [];

    if (row.parameter === '' && !row?.values?.every(val => typeof val === 'number' && !isNaN(val))) {
      invalidFields.push('Please select a parameter and Enter values');
    }

    if (row.parameter === '' && row?.values?.every(val => typeof val === 'number' && !isNaN(val))) {
      invalidFields.push('Please select a parameter');
    }

    if (row.parameter !== '' && !row?.values?.every(val => typeof val === 'number' && !isNaN(val))) {
      invalidFields.push(`Please enter valid values for: ${row?.parameter}`);
    }

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return validateParameterDetails(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
            `${invalidFields.join(', ')}`,
            'error',
            new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const saveHydraulicsParameter = (row) => {
    if (checkHydraulicsParameterValues(row)) {
      const key = Object.keys(PARAMETER_ANALYSIS_TYPE_VALUE).find(
        (k) => PARAMETER_ANALYSIS_TYPE_VALUE[k] === row?.parameter
      ) || row?.parameter;
      const inputs = {
        parameter: key,
        values: unit !== UNIT_SYSTEM.US
          ? row?.values.map((e) =>
            convertUnitSystem(Number(e), hydraulicsParameter?.units?.inputs?.[key]?.conversionFactor)
          )
          : row?.values
      };
      if (row?.isNewRow) {
        const payload = {
          input: inputs,
          wellsInfoId: rowData?.wellsInfoId,
          runId: rowData._id,
        };
        const hydraulicsBody = {
          isParametricResults: rowData?.isParametricResults,
          query: selectedJob?.fromWells
            ? {
                wellsInfoId: selectedJob?.wellsInfoId
              }
            : {
                jobId: selectedJob?._id
              },
        };
        dispatch(createHydraulicParameter(payload, hydraulicsBody));
      } else {
        const payload = {
          input: inputs,
          wellsInfoId: rowData?.wellsInfoId,
          runId: rowData._id,
          _id: row._id
        };
        const hydraulicsBody = {
          isParametricResults: rowData?.isParametricResults,
          query: selectedJob?.fromWells
            ? {
                wellsInfoId: selectedJob?.wellsInfoId
              }
            : {
                jobId: selectedJob?._id
              },
        };
        dispatch(updateHydraulicParameter(payload, hydraulicsBody));
      }
    }
  };

  const removeHydraulicsParameter = (row) => {
    if (row?.isNewRow) {
      const updatedParams = properties.filter(item => item._id !== row?._id);
      setProperties(updatedParams);
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id
      };
      dispatch(listHydraulicsParameter(payload));
    } else {
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData._id,
        _id: row._id
      };
      const hydraulicsBody = {
        isParametricResults: rowData?.isParametricResults,
        query: selectedJob?.fromWells
          ? {
              wellsInfoId: selectedJob?.wellsInfoId
            }
          : {
              jobId: selectedJob?._id
            },
      };
      dispatch(deleteHydraulicParameter(payload, hydraulicsBody));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const renderActionCell = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || hydraulicsParameter.loading}
          onClick={() => {
            saveHydraulicsParameter(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={hydraulicsParameter.loading}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  if (apiRef.current === null) apiRef.current = {};

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={() => {
        onCloseClick();
        setProperties([]);
      }}
      dialogClass={classes.dialogModal}
    >
      <Grid container>
        <Grid item xs={12} marginBottom={'15px'}>
          <Datagrid
            data={runDetails}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem',
                fontWeight: '900 !important'
              },
              '& .MuiDataGrid-footerContainer': {
                display: 'none',
              },
            }}
            loading={false}
            getRowId={(row) => row._id}
            columns={columns(renderCell, hydraulics?.units?.run_details, unit, properties)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            autoheight
            height={'150px'}
            hideFooterPagination
            hideFooterSelectedRowCount
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item className={classes.customTool} justifyContent="space-between" >
          <Grid item>
            <Typography variant='button' fontWeight='700' onClick={handleAddParameter} className={hydraulicsParameter.loading || hydraulicsParameter?.inputs[0]?.isNewRow || properties?.length === MAX_ROW_LIMITS.PARAMETRIC || properties.some(item => item.isDirty === true) ? classes.disableIcon : ''}>+ Add</Typography>
          </Grid>
          <Grid item marginLeft='12px' className={classes.entryLabel}>
            <Typography variant='h6' color={COLORS.LIGHT_GREY} fontWeight='500'>{`(Note: Maximum ${MAX_ROW_LIMITS.PARAMETRIC} entries are allowed)`}</Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900,
                textAlign: 'justify',
                pointerEvents: loader ? 'none' : 'auto',
                backgroundColor: loader ? '#f5f5f5' : 'inherit'
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem !important',
                fontWeight: '900 !important'
              },
            }}
            loading={hydraulicsParameter.loading}
            columns={parametriTablecColumns(renderParameterDropDown, renderValueCell, renderActionCell)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            height={'50vh'}
            isRowSelectable={() => !loader}
            getRowClassName={(params) =>
              loader ? 'Mui-disabled' : (invalidRows[params.id] ? classes.invalidRow : '')
            }
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
          />
        </Grid>
      </Grid>
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={removeHydraulicsParameter}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
      <UnsavedChangesModal />
    </ModalDialog>
  );
};
ParametricAnalysisModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  selectedJob: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  rowData: PropTypes.object
};
ParametricAnalysisModal.defaultProps = {
  selectedJob: {}
};

export default ParametricAnalysisModal;
